import React, { useState } from "react";
import {
  Box,
  Button,
  Checkbox,
  FormControlLabel,
  Grid,
  InputAdornment,
  TextField,
  Typography,
} from "@mui/material";
import { Link, useNavigate } from "react-router-dom";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import GoogleIcon from "@mui/icons-material/Google";
import FacebookIcon from "@mui/icons-material/Facebook";
import TwitterIcon from "@mui/icons-material/Twitter";
import EmailIcon from "@mui/icons-material/Email";
import KeyIcon from "@mui/icons-material/Key";
import VisibilityIcon from "@mui/icons-material/Visibility";
import VisibilityOffIcon from "@mui/icons-material/VisibilityOff";
import CloseIcon from "@mui/icons-material/Close";
import CheckIcon from "@mui/icons-material/Check";
import ErrorIcon from "@mui/icons-material/Error";
import { useAuthData } from "modules/Authentication/hooks/useAuthData";
import { ILoginFormType } from "types";

type Props = {};

export const ResetEmail = (props: Props) => {
  const {
    onLogin,
    loading,
    error,
    userNotRegistered,
    loginWithGoogle,
    loginWithFacebook,
    loginWithTwitter,
    sendPasswordReset,
  } = useAuthData();
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [emailError, setEmailError] = useState("");
  const [passwordError, setPasswordError] = useState("");
  const [isEmailValid, setIsEmailValid] = useState(false);

  const togglePasswordVisibility = () => {
    setShowPassword(!showPassword);
  };

  const handleChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = event.target;
    if (name === "email") {
      setEmail(value);
      validateEmail(value); // Validate email on each change
    } else if (name === "password") {
      setPassword(value);
      validatePassword(value); // Validate password on each change
    }
  };

  const validateEmail = (value: string): void => {
    if (!value) {
      setEmailError("Email is required");
      setIsEmailValid(false);
      return;
    }

    // Basic email format validation using regex
    const regex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!regex.test(value)) {
      setEmailError("Invalid email format");
      setIsEmailValid(false);
      return;
    }

    setEmailError("");
    setIsEmailValid(true);
  };

  const validatePassword = (value?: string): void => {
    if (!value) {
      setPasswordError("Please re-enter your password");
      return;
    }

    // Basic password length validation
    if (value.length < 8) {
      setPasswordError("Minimum 8 characters required");
      return;
    }

    setPasswordError("");
  };
  const handleSubmit = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    validateEmail(email);
    if (emailError) return;

    const result = await sendPasswordReset(email);
    if (result.success) {
      alert("Password reset email sent!");
    } else {
      setEmailError(result.error || "Failed to send password reset email");
      setIsEmailValid(false);
    }
  };

  const handleNavigateBack = () => {
    navigate(-1);
  };

  return (
    <React.Fragment>
      <form onSubmit={handleSubmit} style={styles.container}>
        <Grid item onClick={handleNavigateBack}>
          <ArrowBackIcon sx={{ cursor: "pointer" }} />
        </Grid>
        <Grid
          mt={3}
          container
          spacing={2}
          direction="column"
          justifyContent="center"
          sx={{ minHeight: "55vh" }}
        >
          <Grid item>
            <Box
              fontSize={{ xs: "20.76px", md: "28px" }}
              fontWeight={{ xs: "600", md: "500" }}
              color="black"
            >
              Reset Password
            </Box>
          </Grid>

          <Grid item xs={12} mt={4}>
            <TextField
              type="email"
              name="email"
              variant="outlined"
              fullWidth
              placeholder="Enter your email"
              error={!!emailError}
              value={email}
              onChange={handleChange}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <EmailIcon
                      sx={{
                        color: "#6D6D6D",
                        width: { xs: "11.86px", md: "20px" },
                        height: { xs: "12px", md: "20px" },
                      }}
                    />
                  </InputAdornment>
                ),
                endAdornment: (
                  <InputAdornment position="end">
                    {email && (
                      <Typography
                        sx={{ display: "flex", alignItems: "center" }}
                      >
                        <Box
                          component="span"
                          sx={{ color: isEmailValid ? "#808080" : "red" }}
                        >
                          {isEmailValid ? (
                            <CheckIcon
                              sx={{
                                color: "green",
                                width: { xs: "11.86px", md: "20px" },
                                height: { xs: "12px", md: "20px" },
                              }}
                            />
                          ) : (
                            <CloseIcon
                              sx={{
                                color: "red",
                                width: { xs: "11.86px", md: "20px" },
                                height: { xs: "12px", md: "20px" },
                              }}
                            />
                          )}
                        </Box>
                      </Typography>
                    )}
                  </InputAdornment>
                ),
              }}
              sx={{
                backgroundColor: "white",
                borderRadius: "12px",
                "& .MuiInputBase-root": {
                  height: { xs: "35.39px", md: "48px" },
                },
                "& .MuiInputBase-input::placeholder": {
                  color: "#6D6D6D",
                  fontSize: { xs: "12px", md: "16px" },
                  fontWeight: "500",
                  opacity: 1,
                },
                "& .MuiOutlinedInput-root": {
                  "& fieldset": {
                    borderRadius: { xs: "8.9px", md: "12px" },
                    borderColor: emailError ? "red" : undefined,
                  },
                  "& input": {
                    color: emailError ? "red" : "inherit",
                    // height: '16px',
                  },
                },
              }}
            />
            {emailError && (
              <Typography
                sx={{
                  color: "red",
                  fontSize: "14px",
                  mt: 1,
                  ml: 1,
                }}
              >
                {emailError}
              </Typography>
            )}
          </Grid>

          <Grid item xs={12} mt={3}>
            <Button
              fullWidth
              variant="contained"
              color="primary"
              type="submit"
              sx={{
                backgroundColor: "#233F91",
                borderRadius: { xs: "10px", md: "25px" },
                color: "white",
                height: "48px",
                "&:hover": {
                  backgroundColor: "#EEEEEE",
                  color: "#233F91",
                  border: "1.5px solid #233F91",
                },
              }}
            >
              Reset Password
            </Button>
          </Grid>

          {/* </Box> */}
        </Grid>
      </form>
    </React.Fragment>
  );
};

const styles = {
  link: {
    color: "black",
    textDecoration: "underline",
    fontWeight: "500",
    fontSize: "16px",
  },
  social: {
    display: "flex",
    p: "9px 20px 10px 20px",
    borderRadius: "8px",
    border: "1px solid #E1E1E1",
    boxShadow: 2,
    cursor: "pointer",
  },
  container: {
    height: "80vh",
  },
};

export default ResetEmail;
